import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../config/api";
import axios from "axios";
import { errorMessage } from "../utils/errorMessage";
import {
  Calendar, Clock, Plus, ChevronLeft, ChevronRight, AlertTriangle,
  Check, X, Edit2, Trash2, Loader2, Filter
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { LOGO_URL } from "../config/constants";

const CATEGORIES = [
  { value: "court_filing", label: "Court Filing" },
  { value: "limitation_date", label: "Limitation Date" },
  { value: "exchange_deadline", label: "Exchange Deadline" },
  { value: "completion_date", label: "Completion Date" },
  { value: "search_expiry", label: "Search Expiry" },
  { value: "mortgage_offer", label: "Mortgage Offer" },
  { value: "internal_reminder", label: "Internal Reminder" },
  { value: "regulatory", label: "Regulatory" },
  { value: "client_followup", label: "Client Follow-up" },
  { value: "other", label: "Other" },
];

// Section 2.1: Category colour-coding
const CATEGORY_COLORS = {
  exchange_deadline: { bg: "bg-blue-500/20", text: "text-blue-300", border: "border-blue-500/40", dot: "bg-blue-400", hex: "#3B82F6" },
  completion_date:   { bg: "bg-blue-500/20", text: "text-blue-300", border: "border-blue-500/40", dot: "bg-blue-400", hex: "#3B82F6" },
  limitation_date:   { bg: "bg-status-red/20", text: "text-status-red", border: "border-red-500/40", dot: "bg-status-red", hex: "#EF4444" },
  court_filing:      { bg: "bg-status-red/20", text: "text-status-red", border: "border-red-500/40", dot: "bg-status-red", hex: "#EF4444" },
  search_expiry:     { bg: "bg-status-amber/20", text: "text-status-amber", border: "border-amber-500/40", dot: "bg-status-amber", hex: "#F59E0B" },
  mortgage_offer:    { bg: "bg-status-amber/20", text: "text-status-amber", border: "border-amber-500/40", dot: "bg-status-amber", hex: "#F59E0B" },
  regulatory:        { bg: "bg-status-amber/20", text: "text-status-amber", border: "border-amber-500/40", dot: "bg-status-amber", hex: "#F59E0B" },
  internal_reminder: { bg: "bg-slate-500/20", text: "text-slate-300", border: "border-slate-500/40", dot: "bg-slate-400", hex: "#9CA3AF" },
  client_followup:   { bg: "bg-slate-500/20", text: "text-slate-300", border: "border-slate-500/40", dot: "bg-slate-400", hex: "#9CA3AF" },
  other:             { bg: "bg-slate-500/20", text: "text-slate-300", border: "border-slate-500/40", dot: "bg-slate-400", hex: "#9CA3AF" },
};

const getCatColor = (cat) => CATEGORY_COLORS[cat] || CATEGORY_COLORS.other;

const URGENCY_COLORS = {
  red: { bg: "bg-status-red/15", text: "text-status-red", border: "border-red-500/30", dot: "bg-status-red" },
  amber: { bg: "bg-status-amber/15", text: "text-status-amber", border: "border-amber-500/30", dot: "bg-status-amber" },
  green: { bg: "bg-teal/15", text: "text-teal", border: "border-teal/30", dot: "bg-teal" },
};

const STATUS_STYLES = {
  pending: { bg: "bg-blue-500/15", text: "text-blue-400" },
  overdue: { bg: "bg-status-red/15", text: "text-status-red" },
  completed: { bg: "bg-teal/15", text: "text-teal" },
  cancelled: { bg: "bg-slate-500/15", text: "text-slate-400" },
};

// Section 2.2: Fee earner initials helper
const getInitials = (name) => {
  if (!name) return "";
  // If already looks like initials (2-4 uppercase letters), return as-is
  if (/^[A-Z]{2,4}$/.test(name.trim())) return name.trim();
  // If it's an email, take first 2 letters of local part
  if (name.includes("@")) return name.split("@")[0].slice(0, 2).toUpperCase();
  // Otherwise split and take first letter of each word
  return name.split(/\s+/).map(w => w[0]).join("").toUpperCase().slice(0, 3);
};

export default function DeadlineTracker() {
  const navigate = useNavigate();
  const [view, setView] = useState("list"); // "list" | "calendar"
  const [deadlines, setDeadlines] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const [sortField, setSortField] = useState("due_date");
  const [sortDir, setSortDir] = useState("asc");
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  const [form, setForm] = useState({
    matter_id: "",
    description: "",
    due_date: "",
    category: "internal_reminder",
    assigned_to: "",
    notes: "",
  });

  const userId = sessionStorage.getItem("bonsai_user_id");
  const headers = { "X-Bonsai-User-Id": userId };

  const fetchDeadlines = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/hub/deadlines/upcoming?days=90`, { headers });
      setDeadlines(resp.data.deadlines || []);
    } catch (err) {
      console.error("Failed to fetch deadlines:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCalendar = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/hub/deadlines/calendar?month=${currentMonth}`, { headers });
      setCalendarData(resp.data.deadlines_by_date || {});
    } catch (err) {
      console.error("Failed to fetch calendar:", err);
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchDeadlines();
  }, [fetchDeadlines]);

  useEffect(() => {
    if (view === "calendar") fetchCalendar();
  }, [view, fetchCalendar]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim() || !form.due_date) {
      toast.error("Description and due date are required");
      return;
    }
    try {
      if (editingId) {
        await axios.patch(`${API}/hub/deadlines/${editingId}`, form, { headers });
        toast.success("Deadline updated");
      } else {
        await axios.post(`${API}/hub/deadlines`, form, { headers });
        toast.success("Deadline created");
      }
      resetForm();
      fetchDeadlines();
      if (view === "calendar") fetchCalendar();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save deadline"));
    }
  };

  const handleComplete = async (id) => {
    try {
      await axios.patch(`${API}/hub/deadlines/${id}`, { status: "completed" }, { headers });
      toast.success("Deadline completed");
      fetchDeadlines();
      if (view === "calendar") fetchCalendar();
    } catch {
      toast.error("Failed to complete deadline");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/hub/deadlines/${id}`, { headers });
      toast.success("Deadline cancelled");
      fetchDeadlines();
      if (view === "calendar") fetchCalendar();
    } catch {
      toast.error("Failed to cancel deadline");
    }
  };

  const startEdit = (d) => {
    setForm({
      matter_id: d.matter_id || "",
      description: d.description || "",
      due_date: d.due_date?.slice(0, 16) || "",
      category: d.category || "internal_reminder",
      assigned_to: d.assigned_to || "",
      notes: d.notes || "",
    });
    setEditingId(d.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setForm({ matter_id: "", description: "", due_date: "", category: "internal_reminder", assigned_to: "", notes: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const navigateMonth = (dir) => {
    const [y, m] = currentMonth.split("-").map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const filtered = (filterCategory === "all"
    ? deadlines
    : deadlines.filter((d) => d.category === filterCategory)
  ).sort((a, b) => {
    const av = a[sortField] || "";
    const bv = b[sortField] || "";
    return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  const monthName = (() => {
    const [y, m] = currentMonth.split("-").map(Number);
    return new Date(y, m - 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  })();

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Header */}
      <nav className="glass-nav sticky top-0 z-50 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Bonsai Law" className="w-10 h-10" />
              <div>
                <button onClick={() => window.location.href="/"} className="mb-3 text-xs text-teal hover:text-teal flex items-center gap-1">← Dashboard</button>
        <h1 className="text-lg font-bold text-teal">Deadline Tracker</h1>
                <p className="text-[11px] text-txt-muted tracking-wide font-medium">CaseMinder Hub</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/")}
                className="rounded-md text-txt-muted border-navy-border hover:bg-navy-hover hover:text-white"
                data-testid="back-to-dashboard"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Controls Bar */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Button
              variant={view === "list" ? "default" : "outline"}
              size="sm"
              onClick={() => setView("list")}
              className={view === "list" ? "bg-teal text-white" : "text-txt-muted border-navy-border"}
              data-testid="view-list-btn"
            >
              <Clock className="w-4 h-4 mr-1.5" />
              List
            </Button>
            <Button
              variant={view === "calendar" ? "default" : "outline"}
              size="sm"
              onClick={() => setView("calendar")}
              className={view === "calendar" ? "bg-teal text-white" : "text-txt-muted border-navy-border"}
              data-testid="view-calendar-btn"
            >
              <Calendar className="w-4 h-4 mr-1.5" />
              Calendar
            </Button>
            <div className="ml-2">
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[180px] bg-navy border-navy-border text-txt text-xs" data-testid="filter-category">
                  <Filter className="w-3 h-3 mr-1.5 text-txt-muted" />
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent className="bg-navy border-navy-border">
                  <SelectItem value="all" className="text-txt">All Categories</SelectItem>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value} className="text-txt">{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-teal hover:bg-teal-hover text-txt-faint font-bold gap-1.5"
            data-testid="add-deadline-btn"
          >
            <Plus className="w-4 h-4" />
            New Deadline
          </Button>
        </div>

        {/* Create / Edit Form */}
        {showForm && (
          <div className="glass-card rounded-md p-5 mb-6 animate-fade-in-up" data-testid="deadline-form">
            <h3 className="text-sm font-bold text-white mb-4">
              {editingId ? "Edit Deadline" : "Create Deadline"}
            </h3>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                placeholder="Matter ID or Reference"
                value={form.matter_id}
                onChange={(e) => setForm({ ...form, matter_id: e.target.value })}
                className="bg-navy border-navy-border text-txt text-sm"
                data-testid="form-matter-id"
              />
              <Input
                placeholder="Description *"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-navy border-navy-border text-txt text-sm"
                required
                data-testid="form-description"
              />
              <Input
                type="datetime-local"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="bg-navy border-navy-border text-txt text-sm"
                required
                data-testid="form-due-date"
              />
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-navy border-navy-border text-txt text-sm" data-testid="form-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-navy border-navy-border">
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value} className="text-txt">{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="Assigned to (email)"
                value={form.assigned_to}
                onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                className="bg-navy border-navy-border text-txt text-sm"
                data-testid="form-assigned-to"
              />
              <Input
                placeholder="Notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-navy border-navy-border text-txt text-sm"
                data-testid="form-notes"
              />
              <div className="sm:col-span-2 flex gap-2 justify-end">
                <Button type="button" variant="outline" size="sm" onClick={resetForm} className="text-txt-muted border-navy-border" data-testid="form-cancel">
                  Cancel
                </Button>
                <Button type="submit" size="sm" className="bg-teal hover:bg-teal-hover text-white" data-testid="form-submit">
                  {editingId ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-txt-muted gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading deadlines...</span>
          </div>
        ) : view === "list" ? (
          /* List View — sortable table */
          <div className="space-y-0" data-testid="deadlines-list">
            {filtered.length === 0 ? (
              <div className="glass-card rounded-md p-10 text-center">
                <Calendar className="w-10 h-10 text-txt-faint mx-auto mb-3" />
                <p className="text-txt-muted text-sm">No deadlines found</p>
                <p className="text-txt-muted text-xs mt-1">Click "New Deadline" to create one</p>
              </div>
            ) : (
              <div className="glass-card rounded-md overflow-hidden">
                {/* Sortable header — matches spec: Due Date, Category, Matter Ref, Description, Assigned To, Status, Actions */}
                <div className="grid grid-cols-[90px_110px_100px_1fr_90px_70px_80px] gap-2 px-4 py-2 text-[10px] font-bold text-txt-muted uppercase tracking-wider border-b border-navy-border bg-navy">
                  <button className="text-left flex items-center gap-1 hover:text-white" onClick={() => toggleSort("due_date")} data-testid="sort-due-date">
                    Due Date {sortField === "due_date" ? (sortDir === "asc" ? "\u2191" : "\u2193") : ""}
                  </button>
                  <button className="text-left flex items-center gap-1 hover:text-white" onClick={() => toggleSort("category")} data-testid="sort-category">
                    Category {sortField === "category" ? (sortDir === "asc" ? "\u2191" : "\u2193") : ""}
                  </button>
                  <button className="text-left flex items-center gap-1 hover:text-white" onClick={() => toggleSort("matter_id")} data-testid="sort-matter-ref">
                    Matter Ref {sortField === "matter_id" ? (sortDir === "asc" ? "\u2191" : "\u2193") : ""}
                  </button>
                  <button className="text-left flex items-center gap-1 hover:text-white" onClick={() => toggleSort("description")} data-testid="sort-description">
                    Description {sortField === "description" ? (sortDir === "asc" ? "\u2191" : "\u2193") : ""}
                  </button>
                  <button className="text-left flex items-center gap-1 hover:text-white" onClick={() => toggleSort("assigned_to")} data-testid="sort-assigned">
                    Assigned {sortField === "assigned_to" ? (sortDir === "asc" ? "\u2191" : "\u2193") : ""}
                  </button>
                  <span>Status</span>
                  <span>Actions</span>
                </div>
                {filtered.map((d) => {
                  const cat = getCatColor(d.category);
                  const st = STATUS_STYLES[d.status] || STATUS_STYLES.pending;
                  const catLabel = CATEGORIES.find((c) => c.value === d.category)?.label || d.category;
                  const initials = getInitials(d.assigned_to);
                  return (
                    <div
                      key={d.id}
                      className={`grid grid-cols-[90px_110px_100px_1fr_90px_70px_80px] gap-2 px-4 py-2.5 items-center border-b border-navy-border/30 hover:bg-navy-hover transition-colors`}
                      style={{ borderLeft: `3px solid ${cat.hex}` }}
                      data-testid={`deadline-${d.id}`}
                    >
                      <span className="text-xs text-txt-muted font-mono">{d.due_date?.slice(0, 10)}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded w-fit ${cat.bg} ${cat.text}`}>{catLabel}</span>
                      <span className="text-xs text-txt-muted font-mono truncate" title={d.matter_id}>{d.matter_id || "—"}</span>
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cat.dot}`} />
                        <p className="text-sm text-white truncate">{d.description}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {initials && (
                          <span className="w-6 h-6 rounded-full bg-navy-hover border border-navy-border flex items-center justify-center text-[9px] font-bold text-txt-muted" title={d.assigned_to}>
                            {initials}
                          </span>
                        )}
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded w-fit ${st.bg} ${st.text}`}>{d.status}</span>
                      <div className="flex items-center gap-1">
                        {d.status !== "completed" && (
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-teal hover:text-teal hover:bg-teal-dim/20" onClick={() => handleComplete(d.id)} title="Mark complete" data-testid={`complete-${d.id}`}>
                            <Check className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-txt-muted hover:text-white hover:bg-navy-hover" onClick={() => startEdit(d)} title="Edit" data-testid={`edit-${d.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-status-red/60 hover:text-status-red hover:bg-status-red-dim/20" onClick={() => handleDelete(d.id)} title="Cancel" data-testid={`delete-${d.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          /* Calendar View */
          <div data-testid="deadlines-calendar">
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)} className="text-txt-muted" data-testid="cal-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h3 className="text-base font-bold text-white" data-testid="cal-month">{monthName}</h3>
              <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)} className="text-txt-muted" data-testid="cal-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <CalendarGrid month={currentMonth} data={calendarData} onComplete={handleComplete} onEdit={startEdit} onDelete={handleDelete} />
          </div>
        )}
      </main>
    </div>
  );
}

function CalendarGrid({ month, data, onComplete, onEdit, onDelete }) {
  const [y, m] = month.split("-").map(Number);
  const firstDay = new Date(y, m - 1, 1);
  const daysInMonth = new Date(y, m, 0).getDate();
  const startWeekday = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Mon start
  const today = new Date().toISOString().slice(0, 10);

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="glass-card rounded-md overflow-hidden">
      <div className="grid grid-cols-7 text-center text-[10px] font-bold text-txt-muted uppercase tracking-wider border-b border-navy-border">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="p-2">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((day, i) => {
          if (day === null) return <div key={`empty-${i}`} className="border-b border-r border-navy-border/30 min-h-[80px] bg-navy" />;
          const dateStr = `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const items = data[dateStr] || [];
          const isToday = dateStr === today;
          return (
            <div
              key={dateStr}
              className={`border-b border-r border-navy-border/30 min-h-[80px] p-1.5 ${isToday ? "bg-teal/10" : ""}`}
              data-testid={`cal-day-${dateStr}`}
            >
              <p className={`text-xs font-mono mb-1 ${isToday ? "text-teal font-bold" : "text-txt-muted"}`}>{day}</p>
              {items.slice(0, 4).map((dl) => {
                const cat = getCatColor(dl.category);
                const initials = getInitials(dl.assigned_to);
                return (
                  <div
                    key={dl.id}
                    className={`text-[9px] px-1 py-0.5 mb-0.5 rounded truncate cursor-pointer flex items-center gap-0.5 ${cat.bg} ${cat.text}`}
                    title={dl.description}
                    onClick={() => onEdit(dl)}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cat.dot}`} />
                    {initials ? `${initials} - ` : ""}{dl.description}
                  </div>
                );
              })}
              {items.length > 4 && (
                <button className="text-[9px] text-teal hover:underline w-full text-left" onClick={() => onEdit(items[4])} data-testid={`cal-more-${dateStr}`}>
                  +{items.length - 4} more
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
